import React, { useEffect, useState, useCallback } from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { Cookie, X, Shield, BarChart3, Megaphone, Check } from "lucide-react";

const STORAGE_KEY = "cc_cookie_consent_v1";
const CONSENT_VERSION = 1;

const defaultConsent = {
  version: CONSENT_VERSION,
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

const writeConsent = (data) => {
  try {
    const payload = { ...data, version: CONSENT_VERSION, timestamp: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent("cc-cookie-consent", { detail: payload }));
  } catch (e) {
    /* ignore */
  }
};

const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(defaultConsent);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setPrefs(existing);
    }

    const openHandler = () => {
      const cur = readConsent() || defaultConsent;
      setPrefs(cur);
      setPrefsOpen(true);
      setVisible(true);
    };
    window.addEventListener("cc-open-cookie-prefs", openHandler);
    return () => window.removeEventListener("cc-open-cookie-prefs", openHandler);
  }, []);

  const acceptAll = useCallback(() => {
    const p = { ...defaultConsent, analytics: true, marketing: true };
    setPrefs(p);
    writeConsent(p);
    setVisible(false);
    setPrefsOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const p = { ...defaultConsent };
    setPrefs(p);
    writeConsent(p);
    setVisible(false);
    setPrefsOpen(false);
  }, []);

  const savePrefs = useCallback(() => {
    writeConsent(prefs);
    setVisible(false);
    setPrefsOpen(false);
  }, [prefs]);

  if (!visible) return null;

  const CATS = [
    { key: "necessary", icon: Shield, title: t("cookie.catNecessary"), desc: t("cookie.catNecessaryDesc"), required: true },
    { key: "analytics", icon: BarChart3, title: t("cookie.catAnalytics"), desc: t("cookie.catAnalyticsDesc") },
    { key: "marketing", icon: Megaphone, title: t("cookie.catMarketing"), desc: t("cookie.catMarketingDesc") },
  ];

  return (
    <>
      {prefsOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-[2px]"
          onClick={() => setPrefsOpen(false)}
          aria-hidden="true"
        />
      )}

      {!prefsOpen && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label={t("cookie.dialogLabel")}
          className="fixed bottom-0 left-0 right-0 z-[70] p-3 sm:p-5"
          data-testid="cookie-banner"
        >
          <div
            className="mx-auto max-w-5xl rounded-2xl overflow-hidden shadow-2xl"
            style={{
              background: "#2a160c",
              color: "#f5ead6",
              border: "1px solid rgba(245,234,214,0.12)",
              boxShadow: "0 30px 60px -20px rgba(0,0,0,0.55)",
            }}
          >
            <div className="h-1 w-full" style={{ background: "#c5371f" }} />
            <div className="p-5 sm:p-6 lg:p-7 flex flex-col lg:flex-row lg:items-center gap-5">
              <div className="flex items-start gap-4 flex-1">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "#c5371f" }}
                >
                  <Cookie size={20} color="#fff" />
                </div>
                <div className="min-w-0">
                  <p className="font-serif-display font-bold text-lg leading-tight" style={{ color: "#fbf5e9" }}>
                    {t("cookie.title")}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "#ecdcbd" }}>
                    {t("cookie.desc")}{" "}
                    {t("cookie.consult")}{" "}
                    <Link to="/cookie-policy" className="underline hover:text-white" style={{ color: "#c5371f" }}>
                      {t("cookie.cookiePolicy")}
                    </Link>{" "}
                    {t("cookie.and")}{" "}
                    <Link to="/privacy-policy" className="underline hover:text-white" style={{ color: "#c5371f" }}>
                      {t("cookie.privacyPolicy")}
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 shrink-0">
                <button
                  onClick={() => setPrefsOpen(true)}
                  className="px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition focus-ring"
                  style={{ background: "transparent", color: "#f5ead6", border: "1.5px solid rgba(245,234,214,0.35)" }}
                  data-testid="cookie-btn-customize"
                >
                  {t("cookie.customize")}
                </button>
                <button
                  onClick={rejectAll}
                  className="px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition focus-ring hover:bg-[#3b2417]"
                  style={{ background: "transparent", color: "#f5ead6", border: "1.5px solid rgba(245,234,214,0.35)" }}
                  data-testid="cookie-btn-reject"
                >
                  {t("cookie.reject")}
                </button>
                <button
                  onClick={acceptAll}
                  className="btn-red px-6 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider focus-ring inline-flex items-center gap-2"
                  data-testid="cookie-btn-accept"
                >
                  <Check size={14} /> {t("cookie.acceptAll")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {prefsOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("cookie.prefsDialogLabel")}
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6"
          data-testid="cookie-prefs-modal"
        >
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
            style={{ background: "#fbf5e9", color: "#2a160c", boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6)" }}
          >
            <div className="h-1 w-full" style={{ background: "#c5371f" }} />
            <div className="px-6 sm:px-8 pt-6 pb-4 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#c5371f" }}>
                    <Cookie size={18} color="#fff" />
                  </div>
                  <p className="font-serif-display uppercase tracking-[0.3em] text-[11px]" style={{ color: "#c5371f" }}>
                    {t("cookie.prefsEyebrow")}
                  </p>
                </div>
                <h2
                  className="font-serif-display font-bold mt-2 leading-tight"
                  style={{ color: "#2a160c", fontSize: "clamp(1.4rem, 2.4vw, 1.9rem)" }}
                >
                  {t("cookie.prefsTitle")}
                </h2>
              </div>
              <button
                onClick={() => setPrefsOpen(false)}
                className="p-2 rounded-full hover:bg-[#ecdcbd] transition"
                aria-label={t("cookie.close")}
                data-testid="cookie-prefs-close"
              >
                <X size={20} style={{ color: "#3b2417" }} />
              </button>
            </div>

            <div className="px-6 sm:px-8 overflow-y-auto">
              <p className="text-sm leading-relaxed" style={{ color: "#3b2417" }}>
                {t("cookie.prefsDesc")}{" "}
                <Link to="/cookie-policy" className="underline" style={{ color: "#c5371f" }}>
                  {t("cookie.cookiePolicy")}
                </Link>
                .
              </p>

              <ul className="mt-5 space-y-3">
                {CATS.map(({ key, icon: Icon, title, desc, required }) => (
                  <li
                    key={key}
                    className="rounded-xl p-4 sm:p-5"
                    style={{ background: "#f5ead6", border: "1px solid rgba(91,54,33,0.15)" }}
                    data-testid={`cookie-cat-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "#c5371f" }}>
                        <Icon size={16} color="#fff" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-serif-display font-bold" style={{ color: "#2a160c" }}>{title}</p>
                          <button
                            role="switch"
                            aria-checked={required || !!prefs[key]}
                            disabled={!!required}
                            onClick={() => !required && setPrefs({ ...prefs, [key]: !prefs[key] })}
                            className={`relative w-11 h-6 rounded-full transition-all shrink-0 ${
                              required ? "cursor-not-allowed opacity-70" : "cursor-pointer"
                            }`}
                            style={{ background: required || prefs[key] ? "#c5371f" : "#d5bfa1" }}
                            data-testid={`cookie-switch-${key}`}
                          >
                            <span
                              className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all"
                              style={{ left: required || prefs[key] ? "22px" : "2px" }}
                            />
                          </button>
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "#5b3621" }}>{desc}</p>
                        {required && (
                          <span
                            className="inline-block mt-2 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: "#2a160c", color: "#f5ead6" }}
                          >
                            {t("cookie.alwaysActive")}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="px-6 sm:px-8 py-5 mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3"
              style={{ borderTop: "1px solid rgba(91,54,33,0.15)", background: "#fbf5e9" }}
            >
              <button
                onClick={rejectAll}
                className="px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition hover:bg-[#ecdcbd]"
                style={{ background: "transparent", color: "#3b2417", border: "1.5px solid rgba(91,54,33,0.3)" }}
              >
                {t("cookie.rejectAll")}
              </button>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <button
                  onClick={savePrefs}
                  className="px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition hover:bg-[#ecdcbd]"
                  style={{ background: "transparent", color: "#3b2417", border: "1.5px solid rgba(91,54,33,0.3)" }}
                  data-testid="cookie-prefs-save"
                >
                  {t("cookie.savePrefs")}
                </button>
                <button
                  onClick={acceptAll}
                  className="btn-red px-6 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2"
                  data-testid="cookie-prefs-accept-all"
                >
                  <Check size={14} /> {t("cookie.acceptAll")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;

export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent("cc-open-cookie-prefs"));
};
